/**
 * API Error Handler Utility
 * Provides centralized error handling for API requests with retry logic
 */

export const API_ERROR_TYPES = {
  RATE_LIMIT: 'RATE_LIMIT',
  NETWORK: 'NETWORK',
  SERVER: 'SERVER',
  CLIENT: 'CLIENT',
  TIMEOUT: 'TIMEOUT'
};

export const getErrorType = (error) => {
  if (error.response) {
    const status = error.response.status;
    if (status === 429) return API_ERROR_TYPES.RATE_LIMIT;
    if (status >= 500) return API_ERROR_TYPES.SERVER;
    if (status >= 400) return API_ERROR_TYPES.CLIENT;
  }
  
  if (error.code === 'ECONNABORTED') return API_ERROR_TYPES.TIMEOUT;
  if (error.message === 'Network Error') return API_ERROR_TYPES.NETWORK;
  
  return API_ERROR_TYPES.NETWORK;
};

export const shouldRetry = (error, retryCount, maxRetries = 3) => {
  const errorType = getErrorType(error);
  
  // Only retry for rate limits, network errors, and server errors
  const retryableErrors = [
    API_ERROR_TYPES.RATE_LIMIT,
    API_ERROR_TYPES.NETWORK,
    API_ERROR_TYPES.SERVER
  ];
  
  return retryableErrors.includes(errorType) && retryCount < maxRetries;
};

export const getRetryDelay = (retryCount, errorType) => {
  // Different delay strategies based on error type
  switch (errorType) {
    case API_ERROR_TYPES.RATE_LIMIT:
      // Exponential backoff for rate limits: 1s, 2s, 4s
      return Math.pow(2, retryCount) * 1000;
    
    case API_ERROR_TYPES.NETWORK:
      // Linear backoff for network errors: 2s, 4s, 6s
      return (retryCount + 1) * 2000;
    
    case API_ERROR_TYPES.SERVER:
      // Exponential backoff for server errors: 1s, 3s, 9s
      return Math.pow(3, retryCount) * 1000;
    
    default:
      return 1000;
  }
};

export const logApiError = (error, context = '') => {
  const errorType = getErrorType(error);
  const status = error.response?.status;
  const url = error.config?.url;
  
  const logMessage = [
    `API Error [${errorType}]`,
    context && `Context: ${context}`,
    url && `URL: ${url}`,
    status && `Status: ${status}`,
    `Message: ${error.message}`
  ].filter(Boolean).join(' | ');
  
  if (errorType === API_ERROR_TYPES.RATE_LIMIT) {
    console.warn(logMessage);
  } else {
    console.error(logMessage);
  }
};

export const createFallbackData = (dataType) => {
  const fallbacks = {
    config: {
      business_name: '6ammart',
      base_urls: {
        business_logo_url: process.env.REACT_APP_BASE_URL || 'https://admin.alot263.com',
        item_image_url: `${process.env.REACT_APP_BASE_URL || 'https://admin.alot263.com'}/storage/app/public/product`,
        store_image_url: `${process.env.REACT_APP_BASE_URL || 'https://admin.alot263.com'}/storage/app/public/store`,
        store_cover_photo_url: `${process.env.REACT_APP_BASE_URL || 'https://admin.alot263.com'}/storage/app/public/store/cover`,
        delivery_man_image_url: `${process.env.REACT_APP_BASE_URL || 'https://admin.alot263.com'}/storage/app/public/delivery-man`,
        chat_image_url: `${process.env.REACT_APP_BASE_URL || 'https://admin.alot263.com'}/storage/app/public/conversation`,
        campaign_image_url: `${process.env.REACT_APP_BASE_URL || 'https://admin.alot263.com'}/storage/app/public/campaign`,
        banner_image_url: `${process.env.REACT_APP_BASE_URL || 'https://admin.alot263.com'}/storage/app/public/banner`,
        category_image_url: `${process.env.REACT_APP_BASE_URL || 'https://admin.alot263.com'}/storage/app/public/category`,
        customer_image_url: `${process.env.REACT_APP_BASE_URL || 'https://admin.alot263.com'}/storage/app/public/profile`,
        notification_image_url: `${process.env.REACT_APP_BASE_URL || 'https://admin.alot263.com'}/storage/app/public/notification`,
        vendor_image_url: `${process.env.REACT_APP_BASE_URL || 'https://admin.alot263.com'}/storage/app/public/vendor`,
        coupon_image_url: `${process.env.REACT_APP_BASE_URL || 'https://admin.alot263.com'}/storage/app/public/coupon`,
        review_image_url: `${process.env.REACT_APP_BASE_URL || 'https://admin.alot263.com'}/storage/app/public/review`,
        react_landing_page_images: `${process.env.REACT_APP_BASE_URL || 'https://admin.alot263.com'}/storage/app/public/react_landing`,
        react_landing_page_feature_images: `${process.env.REACT_APP_BASE_URL || 'https://admin.alot263.com'}/storage/app/public/react_landing/feature`
      },
      fav_icon: 'favicon.ico',
      maintenance_mode: false,
      loyalty_point_status: 1,
      guest_checkout_status: 1,
    },
    landingPage: {
      banner_section: [],
      featured_categories: [],
      featured_stores: [],
      popular_items: [],
    },
    discountedItems: {
      products: [],
      stores: [],
      total_size: 0
    },
    emptyList: []
  };
  
  return fallbacks[dataType] || null;
};